import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import {
  CommonLayout,
  StyledContentContainer,
  StyledTitle,
  StyledText,
  StyledLink,
  StyledBoldText,
  StyledEmailSentLogo,
  StyledToast,
  SniperLink,
} from '@selfserve/react-ui';
import { buttonIds } from '@selfserve/react-utils';
import { withErrorHandling } from '@selfserve/react-hocs';
import { useEmailResend } from '@selfserve/react-hooks';
import { useGetLoginUrl } from '@selfserve/react-feature-purchase-plan';

import {
  EmailValidation,
  EmailValidationPayload,
  useEmailValidationRequest,
} from '../data';
import { useCallback, useState } from 'react';

const StyledOneLastStepContainer = styled(StyledContentContainer)(
  ({ theme, isSecondary }: { theme?: any; isSecondary?: boolean }) => ({
    ...(isSecondary && {
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        '.email-sent-logo': {
          marginRight: '76px',
        },
      },
    }),
  })
);

const StyledOneLastStepContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledOneLastStepText = styled(StyledText)(
  ({ theme, isGrid }: { theme?: any; isGrid?: boolean }) => ({
    marginTop: theme.spacing(2),
    display: 'grid',
    ...(isGrid && {
      [theme.breakpoints.down('sm')]: {
        display: 'block',
      },
    }),
  })
);

interface Props {
  isSecondary?: boolean;
}

export const OneLastStep = withErrorHandling<Props>(
  ({ openErrorModal, isSecondary }) => {
    const { t } = useTranslation();
    const { removeSuccessMessage, onClickHandle, successMessage, email } =
      useEmailResend<EmailValidationPayload, EmailValidation>(
        openErrorModal,
        useEmailValidationRequest
      );
    const { redirectToLoginPage, loginPage, requestType } = useGetLoginUrl();
    const [kmeLoginTrigger, setKmeLoginTrigger] = useState(false);

    const redirectToPageDependent = useCallback(() => {
      if (requestType === 'POST') {
        redirectToLoginPage();
      } else {
        setKmeLoginTrigger(true);
      }
    }, [requestType, setKmeLoginTrigger, redirectToLoginPage]);

    return (
      <>
        {requestType === 'POST' && loginPage && loginPage(kmeLoginTrigger)}
        {successMessage && (
          <StyledToast onClose={removeSuccessMessage} severity="success">
            <span>{successMessage}</span>
          </StyledToast>
        )}
        <CommonLayout>
          <StyledOneLastStepContainer isSecondary={isSecondary} centered>
            <StyledEmailSentLogo className="email-sent-logo" />
            <StyledOneLastStepContentContainer>
              <StyledTitle as="span" className="main-header" centered>
                {t('common.one-last-step')}
              </StyledTitle>
              <StyledOneLastStepText centered isGrid>
                {`${t('common.please-verify-email')} `}
                <StyledBoldText className="email-address">
                  {email}
                </StyledBoldText>
              </StyledOneLastStepText>
              <StyledOneLastStepText centered>
                {`${t('common.cant-find-it')} `}
                <StyledLink
                  to=""
                  onClick={onClickHandle}
                  id={buttonIds.resendVerification}
                >
                  {t('common.resend')}
                </StyledLink>
              </StyledOneLastStepText>
              <SniperLink email={email} />
              <StyledOneLastStepText centered>
                {`${t('common.already-verified')} `}
                <StyledLink to="" onClick={redirectToPageDependent}>
                  {t('common.login')}
                </StyledLink>
              </StyledOneLastStepText>
            </StyledOneLastStepContentContainer>
          </StyledOneLastStepContainer>
        </CommonLayout>
      </>
    );
  }
);
